import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Alert,
  CircularProgress
} from '@mui/material';
import { riderService } from '../services/riderService';

const RiderProfileEditModal = ({ open, onClose, profile, onProfileUpdated }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    phone: '',
    vehicleInfo: {
      type: '',
      plateNumber: '',
      color: '',
      model: ''
    }
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (profile && open) {
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        phone: profile.phone || '',
        vehicleInfo: {
          type: profile.vehicleInfo?.type || '',
          plateNumber: profile.vehicleInfo?.plateNumber || '',
          color: profile.vehicleInfo?.color || '',
          model: profile.vehicleInfo?.model || ''
        }
      });
      setError(null);
      setSuccess(false);
    }
  }, [profile, open]);

  const handleInputChange = (field, value) => {
    if (field.startsWith('vehicleInfo.')) {
      const vehicleField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        vehicleInfo: {
          ...prev.vehicleInfo,
          [vehicleField]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setSuccess(false);

    try {
      // Validate required fields
      if (!formData.firstName || !formData.lastName || !formData.phone || !formData.vehicleInfo.type) {
        throw new Error('Please fill in all required fields');
      }

      const response = await riderService.updateProfile(formData);
      console.log('Profile updated successfully:', response);
      
      setSuccess(true);
      
      // Call the callback to refresh the profile data
      if (onProfileUpdated) {
        onProfileUpdated(response);
      }
      
      // Close modal after a short delay
      setTimeout(() => {
        onClose();
      }, 1500);
      
    } catch (error) {
      console.error('Error updating profile:', error);
      setError(error.response?.data?.message || error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" component="div">
          Edit Profile
        </Typography>
      </DialogTitle>
      
      <form onSubmit={handleSubmit}>
        <DialogContent>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          
          {success && (
            <Alert severity="success" sx={{ mb: 2 }}>
              Profile updated successfully!
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="First Name"
                value={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                required
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Last Name"
                value={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                required
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={(e) => handleInputChange('phone', e.target.value)}
                required
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid size={{ xs: 12 }}>
              <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                Vehicle Information
              </Typography>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <FormControl fullWidth margin="normal" required>
                <InputLabel>Vehicle Type</InputLabel>
                <Select
                  value={formData.vehicleInfo.type}
                  onChange={(e) => handleInputChange('vehicleInfo.type', e.target.value)}
                  label="Vehicle Type"
                  disabled={loading}
                >
                  <MenuItem value="bike">Bike</MenuItem>
                  <MenuItem value="motorcycle">Motorcycle</MenuItem>
                  <MenuItem value="car">Car</MenuItem>
                  <MenuItem value="bicycle">Bicycle</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Plate Number"
                value={formData.vehicleInfo.plateNumber}
                onChange={(e) => handleInputChange('vehicleInfo.plateNumber', e.target.value)}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Vehicle Color"
                value={formData.vehicleInfo.color}
                onChange={(e) => handleInputChange('vehicleInfo.color', e.target.value)}
                disabled={loading}
                margin="normal"
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                fullWidth
                label="Vehicle Model"
                value={formData.vehicleInfo.model}
                onChange={(e) => handleInputChange('vehicleInfo.model', e.target.value)}
                disabled={loading}
                margin="normal"
              />
            </Grid>
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ p: 2 }}>
          <Button 
            onClick={handleClose} 
            disabled={loading}
            color="inherit"
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? 'Updating...' : 'Update Profile'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default RiderProfileEditModal;
